import type { ReactElement } from "react";
import { memo, useMemo } from "react";

import { SWReactIcons } from "@similarweb/icons";
import { Dropdown, IconButton, PixelPlaceholderLoader } from "@similarweb/ui-components";

import { SeparatorItem } from "app/pages/activation/components/Shared/SeparatorItem/SeparatorItem";
import type { KeywordsGroupsSchema } from "app/pages/activation/hooks/advancedKWModal/useKWModalUpdater";
import { translate } from "app/filters/i18nFilter";

import type { KWGroupSchema } from "../../../../types";
import { DROPDOWN_CONFIG, PLACEHOLDER_SIZE } from "../constants";
import { keywordModalTexts } from "../texts";

import * as SC from "./styles";

type KeywordsDropdownSchema = {
    onDropdownItemSelect: (id: string) => void;
    keywordsGroups: KeywordsGroupsSchema;
};

const KeywordsDropdown = ({ onDropdownItemSelect, keywordsGroups }: KeywordsDropdownSchema) => {
    const getItems = (KWList: KWGroupSchema[]) =>
        KWList?.map(({ id, text }) => (
            <SC.DropdownItem
                key={id}
                id={id}
                // @ts-expect-error - Property 'text' does not exist on type DropdownItemProps
                text={text}
            >
                <SWReactIcons
                    iconName="nav-keyword-group"
                    size="sm"
                />
                <SC.ItemText>{text}</SC.ItemText>
            </SC.DropdownItem>
        ));

    const search = (searchText: string, item: ReactElement<{ text: string }>) => {
        const keyword = item?.props?.text;

        if (keyword) {
            return keyword.toLowerCase().indexOf(searchText.toLowerCase()) > -1;
        }

        return false;
    };

    const ownKWList = useMemo(() => keywordsGroups?.ownKWList && getItems(keywordsGroups.ownKWList), [keywordsGroups]);
    const sharedKWList = useMemo(
        () => keywordsGroups?.sharedKWList && getItems(keywordsGroups.sharedKWList),
        [keywordsGroups],
    );

    const isDisabled = !keywordsGroups?.ownKWList?.length && !keywordsGroups?.sharedKWList?.length;

    if (keywordsGroups?.isLoading) {
        return (
            <PixelPlaceholderLoader
                width={PLACEHOLDER_SIZE.width}
                height={PLACEHOLDER_SIZE.height}
            />
        );
    }

    return (
        <SC.KeywordsDropdown>
            <Dropdown
                appendTo="#dropdownAdvanced"
                hasSearch
                width={DROPDOWN_CONFIG.width}
                dropdownPopupHeight={DROPDOWN_CONFIG.dropdownPopupHeight}
                dropdownPopupPlacement="ontop-left"
                onClick={(item) => onDropdownItemSelect(item.id)}
                searchPlaceHolder={translate(keywordModalTexts.searchPlaceholder)}
                searchFilter={search}
                disabled={isDisabled}
            >
                {[
                    <IconButton
                        type="flat"
                        iconName="nav-keyword-group"
                        key="keywords-btn-0"
                        isDisabled={isDisabled}
                    >
                        {translate(keywordModalTexts.selectExistingKW)}
                    </IconButton>,
                    <SeparatorItem
                        key="separator1"
                        isVisible={!!keywordsGroups?.ownKWList?.length}
                        text={translate(keywordModalTexts.dropdownSeparatorMyListText)}
                    />,
                    ...ownKWList,
                    <SeparatorItem
                        key="separator2"
                        isVisible={!!keywordsGroups?.sharedKWList?.length}
                        text={translate(keywordModalTexts.dropdownSeparatorSharedListText)}
                    />,
                    ...sharedKWList,
                ]}
            </Dropdown>
        </SC.KeywordsDropdown>
    );
};

export default memo(KeywordsDropdown);
